import { useMutation } from "react-query";
import { CreateEmployerFundTopUpResponse } from "../../models/funding/createEmployerFundTopUpResponse";
import { EmployerFundTopUpStatus } from "../../models/funding/employerFundTopUpStatus";
import { useData } from "../../utils/useData";
import { EmployerPaymentMethodType } from "../../models/funding/employerPaymentMethod";
import { topUpEmployerFund } from "../../mutations/employerFunds/topUpEmployerFund";
import { getSafeSessionConfig } from "../utils/getSafeSessionConfig";
import { useConfig } from "../../utils/useConfig";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

export const useTopUpEmployerFund = (options?: {
    onSuccess?: (data: CreateEmployerFundTopUpResponse) => void;
    onError?: (error: Error) => void;
}) => {
    const { config } = useConfig();
    const { getToken } = useKindeAuth();
    const { employerFund, employerId } = useData();

    return useMutation(
        async ({
            amountWithSurcharge,
            employerPaymentMethodId,
            employerPaymentMethodType,
            reference
          }: {
            amountWithSurcharge: number;
            employerPaymentMethodId?: string;
            employerPaymentMethodType: EmployerPaymentMethodType;
            reference?: string;
          }) => {
            const { API_URL, token } = await getSafeSessionConfig(config, getToken)

            if(!employerFund.data?.id) {
                throw new Error("Employer fund not found");
            }

            return topUpEmployerFund({
                amountWithSurcharge,
                employerPaymentMethodId,
                employerPaymentMethodType,
                reference,
                API_URL,
                token,
                employerId: employerId,
                employerFundId: employerFund.data.id
            });
        },
        {
            onSuccess: (data: CreateEmployerFundTopUpResponse) => {
                if (
                    data.status === EmployerFundTopUpStatus.APPROVED ||
                    data.status === EmployerFundTopUpStatus.PENDING
                ) {
                    employerFund.refetch();
                    options?.onSuccess?.(data);
                } else if (data.status === EmployerFundTopUpStatus.DECLINED) {
                    options?.onError?.(new Error("Payment was declined. Please try again or contact support."));
                } else if (data.status === EmployerFundTopUpStatus.UNSUPPORTED_PAYMENT_METHOD) {
                    options?.onError?.(new Error("This payment method is not supported. Please try a different payment method."));
                }
            },
            onError: () => {
                options?.onError?.(new Error("An error occurred while processing your payment. Please try again or contact support."));
            }
        }
    )
}